import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    Box,
    IconButton,
    SxProps,
    Theme,
    Typography,
    styled,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import {
    DateCalendar,
    PickersCalendarHeaderProps,
} from '@mui/x-date-pickers/DateCalendar';
import moment, { Moment } from 'moment/moment';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REPORT_COLORS } from '../../lobby/constants';
import { ReportDivider } from '../../setting/components/ReportDivider';

import { useObserveElementRect } from '@/hooks/useObserveElementRect';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import './DateRangePicker.scss';
const CustomCalendarHeaderRoot = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 16px',
    alignItems: 'center',
});
const CustomCalendarHeader = (props: PickersCalendarHeaderProps<Moment>) => {
    const { currentMonth, onMonthChange } = props;
    const d = currentMonth.clone();
    const selectNextMonth = () => onMonthChange(d.add({ months: 1 }), 'left');
    const selectPreviousMonth = () =>
        onMonthChange(d.add({ months: -1 }), 'right');
    return (
        <CustomCalendarHeaderRoot className="custom-calendar-header-root">
            <Stack spacing={1} direction="row">
                <IconButton
                    onClick={selectPreviousMonth}
                    title="Previous month"
                >
                    <ChevronLeft />
                </IconButton>
            </Stack>
            <Typography variant="body2" color={'#bf9a5d'}>
                {currentMonth.format('MMMM YYYY')}
            </Typography>
            <Stack spacing={1} direction="row">
                <IconButton onClick={selectNextMonth} title="Next month">
                    <ChevronRight />
                </IconButton>
            </Stack>
        </CustomCalendarHeaderRoot>
    );
};
interface CustomPickerDayProps extends PickersDayProps<Moment> {
    isSelected: boolean;
    isHovered: boolean;
    isFirstSelected: boolean;
    isLastSelected: boolean;
}

const CustomPickersDay = styled(PickersDay, {
    shouldForwardProp: prop =>
        prop !== 'isSelected' &&
        prop !== 'isHovered' &&
        prop !== 'isFirstSelected' &&
        prop !== 'isLastSelected',
})<CustomPickerDayProps>(({ /*theme,*/ isFirstSelected, isLastSelected }) => ({
    borderRadius: 0,
    ...(isFirstSelected && {
        borderRadius: '50%',
        border: '2px solid #BF9A5D',
    }),
    ...(isLastSelected && {
        borderRadius: '50%',
        border: '2px solid #BF9A5D',
    }),
})) as React.ComponentType<CustomPickerDayProps>;

type DayProps = PickersDayProps<Moment> & {
    selectedDay?: Array<Moment>;
    hoveredDay?: Moment;
};
const Day = (props: DayProps) => {
    const { day, selectedDay, hoveredDay, ...other } = props;
    let isSelected = false;
    let isHovered = false;
    let isFirstSelected = false;
    let isLastSelected = false;
    if (selectedDay) {
        isSelected = selectedDay.some(d => d.isSame(day, 'date'));
        // assume selected moment array is sorted
        isFirstSelected = day.isSame(selectedDay[0], 'date');
        isLastSelected = day.isSame(
            selectedDay[selectedDay.length - 1],
            'date'
        );
    }
    if (hoveredDay) {
        isHovered = day.isSame(hoveredDay, 'date');
    }
    /**
     * the container props is for the first and last selected day's background
     * if doesn't add this, the first selected day will missing top-right and bottom-right background...
     */
    const containerProps: SxProps<Theme> = {
        background: isSelected ? '#3E3323' : 'transparent',
    };
    if (isFirstSelected) {
        containerProps.borderTopLeftRadius = '50%';
        containerProps.borderBottomLeftRadius = '50%';
    }
    if (isLastSelected) {
        containerProps.borderTopRightRadius = '50%';
        containerProps.borderBottomRightRadius = '50%';
    }
    return (
        <Box sx={containerProps}>
            <CustomPickersDay
                {...other}
                className="custom-picker-day"
                disableMargin
                day={day}
                selected={false}
                isSelected={isSelected}
                isHovered={isHovered}
                isFirstSelected={isFirstSelected}
                isLastSelected={isLastSelected}
            />
        </Box>
    );
};

type DateRangeDate = Moment | undefined;
type DateRangePickerGetProps = {
    start: DateRangeDate;
    end: DateRangeDate;
    expanded: boolean;
};
type DateRangePickerSetProps = {
    setStart: Dispatch<SetStateAction<DateRangeDate>>;
    setEnd: Dispatch<SetStateAction<DateRangeDate>>;
    setExpanded: Dispatch<SetStateAction<boolean>>;
    handleSubmit?: (startDate: Moment, endDate: Moment) => void;
    playButtonAudio: () => void;
};
type DateRangePickerOwnProps = {
    showPreset: boolean;
};
type DateRangePickerProps = DateRangePickerOwnProps &
    DateRangePickerGetProps &
    DateRangePickerSetProps;

const getSAToday = () => {
    const now = moment();
    let l = moment().zone('+0800').set({ hour: 12, minute: 0, second: 0 });
    const isAfter = now.isAfter(l);
    if (isAfter) l = l.add(1, 'day');
    return l;
};

export const DateRangePicker = (props: DateRangePickerProps) => {
    const {
        start,
        end,
        expanded,
        setStart,
        setEnd,
        setExpanded,
        showPreset,
        handleSubmit,
        playButtonAudio,
    } = props;
    const { t } = useTranslation();
    const [hoveredDay, setHoveredDay] = useState<Moment>();
    const [limit, setLimit] = useState<Moment>();
    const [selectedDay, setSelectedDay] = useState<Array<Moment>>([]);
    const { ref: calendarRef, rect: calendarRect } =
        useObserveElementRect<HTMLDivElement>();
    const [buttonContainerWidth, setButtonContainerWidth] = useState('100%');
    useEffect(() => {
        if (calendarRect !== undefined) {
            const w = calendarRect.width;
            setButtonContainerWidth(`${w}px`);
        }
    }, [calendarRect]);
    useEffect(() => {
        const l = getSAToday();

        console.log('date::picker::limit', l);
        setLimit(l);

        //set default
        const now = getSAToday(); //moment().add(1, 'day');
        const last = moment(now).subtract(7, 'day');
        setStart(last);
        setEnd(now);
        handleSubmit?.(last, now);
    }, []);
    useEffect(() => {
        const s = new Array<Moment>();
        if (start && end) {
            for (
                let m = moment(start);
                m.isSameOrBefore(end, 'date');
                m = m.add(1, 'day')
            ) {
                s.push(moment(m));
            }
        } else if (start) {
            s.push(start);
        }

        setSelectedDay(s);
    }, [start, end]);
    const reset = () => {
        setStart(undefined);
        setEnd(undefined);
        setSelectedDay([]);
    };
    const resetToday = () => {
        const now = getSAToday();
        const last = moment(now).subtract(1, 'day');
        setStart(last);
        setEnd(now);
        setSelectedDay([last, now]);
    };
    const handleChangeDate = (d: Moment) => {
        if (start && end) {
            reset();
            setStart(d);
        } else if (start) {
            setEnd(d);
            handleSubmit?.(start, d);
        } else {
            setStart(d);
        }
    };
    return (
        <>
            <Stack>
                <Accordion expanded={expanded}>
                    <AccordionSummary className="date-range-picker">
                        <Grid className="date-range-container" item container>
                            <Grid
                                className="date-range-list"
                                item
                                container
                                sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setExpanded(!expanded);
                                    playButtonAudio();
                                }}
                            >
                                <DateRangeButton
                                    time={start}
                                    label="12:00:00"
                                />
                                <ChevronRightIcon
                                    sx={{ color: REPORT_COLORS.ARROW }}
                                />
                                <DateRangeButton time={end} label="11:59:59" />
                            </Grid>
                            {showPreset && (
                                <Grid
                                    item
                                    container
                                    className="days-button-list"
                                >
                                    <PresetRangeButton
                                        Last={1}
                                        setStart={setStart}
                                        setEnd={setEnd}
                                        setExpanded={setExpanded}
                                        handleSubmit={handleSubmit}
                                        playButtonAudio={playButtonAudio}
                                    />
                                    <PresetRangeButton
                                        Last={7}
                                        setStart={setStart}
                                        setEnd={setEnd}
                                        setExpanded={setExpanded}
                                        handleSubmit={handleSubmit}
                                        playButtonAudio={playButtonAudio}
                                    />
                                    <PresetRangeButton
                                        Last={14}
                                        setStart={setStart}
                                        setEnd={setEnd}
                                        setExpanded={setExpanded}
                                        handleSubmit={handleSubmit}
                                        playButtonAudio={playButtonAudio}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </AccordionSummary>
                    {/* {expanded && <ReportDivider/>} */}
                    <AccordionDetails className="date-calender-container">
                        <DateCalendar
                            className="date-calender mx-auto"
                            ref={calendarRef}
                            views={['day']}
                            maxDate={limit}
                            onChange={value => {
                                const m = moment(value);
                                handleChangeDate(m);
                            }}
                            slots={{
                                day: Day,
                                calendarHeader: CustomCalendarHeader,
                            }}
                            slotProps={{
                                day: ownerState => ({
                                    selectedDay,
                                    hoveredDay,
                                    onPointerEnter: () =>
                                        setHoveredDay(ownerState.day),
                                    onPointerLeave: () =>
                                        setHoveredDay(undefined),
                                }),
                            }}
                            showDaysOutsideCurrentMonth
                        />
                        {/* <ReportDivider/> */}
                        <Grid
                            container
                            className="calender-button-container mx-auto"
                            width={buttonContainerWidth}
                            marginTop={2}
                        >
                            <Button
                                onClick={() => {
                                    resetToday();
                                    playButtonAudio();
                                }}
                                variant="outlined"
                                className="calender-button"
                            >
                                {/* <RefreshOutlined /> */}
                                {t('report.reset')}
                            </Button>
                            <Button
                                onClick={() => {
                                    setExpanded(false);
                                    playButtonAudio();
                                }}
                                variant="outlined"
                                className="calender-button"
                            >
                                {/* <CloseOutlined /> */}
                                {t('report.close')}
                            </Button>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Stack>
            <ReportDivider />
        </>
    );
};
type PresetRangeButtonOwnProps = {
    Last: 1 | 7 | 14;
};
type PresetRangeButtonProps = DateRangePickerSetProps &
    PresetRangeButtonOwnProps;

const PresetRangeButton = (props: PresetRangeButtonProps) => {
    const {
        Last,
        setStart,
        setEnd,
        setExpanded,
        handleSubmit,
        playButtonAudio,
    } = props;
    const notSingle = Last !== 1;
    const { t } = useTranslation();
    return (
        <Button
            className="preset-range-button"
            variant="outlined"
            onClick={() => {
                const now = getSAToday();
                // const now = Last > 1 ? moment().add(1, 'day') : getSAToday();
                const last = moment(now).subtract(Last, 'day');
                setStart(last);
                setEnd(now);
                setExpanded(false);
                handleSubmit?.(last, now);
                playButtonAudio();
            }}
            sx={{
                direction: 'var(--text-direction)',
            }}
        >
            {t(`betlog.${!notSingle ? 'today' : `last${Last}days`}`)}
        </Button>
    );
};

type DateRangeButtonProps = {
    time: DateRangeDate;
    label: string;
};
const DateRangeButton = (props: DateRangeButtonProps) => {
    const { time, label } = props;
    return (
        <Grid item container className="date-range-button">
            <Grid item className="date-range-button-date-text">
                {moment(time).locale('en').format('YYYY/MM/DD')}
            </Grid>
            <Grid item className="date-range-button-time-text">
                {label}
                <br />
                (UTC+8)
            </Grid>
        </Grid>
    );
};
